import { apiService } from './api';
import { getApiBaseUrl } from '../config/constants';

export interface FeedbackItem {
  id: string;
  userEmail: string;
  location: string;
  type: string;
  feedback: string;
  timestamp: string;
  reviewed: boolean;
  deviceInfo?: any;
}

export interface UserMetrics {
  totalUsers: number;
  activeUsers: number;
  newUsersThisWeek: number;
  newUsersThisMonth: number;
  retentionRate: number;
  returnUsers: number;
  oneTimeUsers: number;
  stepAnalytics: Record<string, {
    users_started: number;
    users_in_progress: number;
    users_completed: number;
    average_days: number;
  }>;
  inventoryAnalytics: {
    resentments: { users_started: number; users_completed: number; avg_entries: number; max_entries: number };
    fears: { users_started: number; users_completed: number; avg_entries: number; max_entries: number };
    harms: { users_started: number; users_completed: number; avg_entries: number; max_entries: number };
  };
  usersCompletedAllInventories: number;
  allInventoriesCompletedRate: number;
  allInventoriesCompletedRateStep4: number;
  usersStartedStep4: number;
  usersWithProgress: number;
}

export interface UserInfo {
  email: string;
  name: string;
  role: string;
  status: 'active' | 'inactive' | 'test';
  registrationDate: string;
  lastLogin: string;
  deactivated_at?: string;
  deactivation_reason?: string;
  isAdmin: boolean;
  admin_rights?: string[];
  emailVerified?: boolean;
  hasProfile?: boolean;
}

export interface ProgramConfig {
  program_id: string;
  title: string;
  subtitle: string;
  logo_url?: string;
  tab1?: any;
  tab2: any;
  tab3: any;
  updated_at: string;
  updated_by: string;
}

export interface ProgramHistoryEntry {
  program_id: string;
  timestamp: string;
  admin_email: string;
  change_summary: string;
}

export interface UserStatusUpdate {
  user_id: string;
  status: 'active' | 'inactive' | 'test';
  deactivation_reason?: string;
}

export class AdminService {
  static async getFeedback(): Promise<FeedbackItem[]> {
    try {
      const response = await apiService.adminRequest('/admin/feedback');
      return response.feedback || [];
    } catch (error) {
      console.error('Error fetching feedback:', error);
      throw error;
    }
  }

  static async getMetrics(): Promise<UserMetrics> {
    try {
      const response = await apiService.adminRequest('/admin/metrics');
      return response.metrics || {
        totalUsers: 0,
        activeUsers: 0,
        newUsersThisWeek: 0,
        stepCompletionRates: {}
      };
    } catch (error) {
      console.error('Error fetching metrics:', error);
      throw error;
    }
  }

  static async getUsers(): Promise<UserInfo[]> {
    try {
      const response = await apiService.adminRequest('/admin/users');
      return response.users || [];
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  static async markFeedbackReviewed(feedbackId: string, reviewed: boolean = true, adminNotes?: string): Promise<void> {
    try {
      await apiService.adminRequest(`/admin/feedback/${feedbackId}/review`, {
        method: 'POST',
        body: JSON.stringify({
          feedback_id: feedbackId,
          reviewed,
          admin_notes: adminNotes
        })
      });
    } catch (error) {
      console.error('Error marking feedback as reviewed:', error);
      throw error;
    }
  }

  static async exportFeedback(): Promise<string> {
    try {
      const response = await apiService.adminRequest('/admin/feedback/export');
      return response.csv || '';
    } catch (error) {
      console.error('Error exporting feedback:', error);
      throw error;
    }
  }

  static async updateUserStatus(userId: string, status: 'active' | 'inactive' | 'test', deactivationReason?: string): Promise<void> {
    try {
      await apiService.adminRequest(`/admin/users/${userId}/status`, {
        method: 'PUT',
        body: JSON.stringify({
          user_id: userId,
          status,
          deactivation_reason: deactivationReason
        })
      });
    } catch (error) {
      console.error('Error updating user status:', error);
      throw error;
    }
  }

  static async deleteUser(userId: string): Promise<void> {
    try {
      await apiService.adminRequest(`/admin/users/${userId}`, {
        method: 'DELETE'
      });
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  static async updateUserRole(userId: string, role: 'user' | 'admin'): Promise<void> {
    try {
      await apiService.adminRequest(`/admin/users/${userId}/role`, {
        method: 'PUT',
        body: JSON.stringify({
          role
        })
      });
    } catch (error) {
      console.error('Error updating user role:', error);
      throw error;
    }
  }

  // Admin Rights Management
  static async getUserAdminRights(userId: string): Promise<{user_id: string; role: string; admin_rights: string[]}> {
    try {
      const response = await apiService.adminRequest(`/admin/users/${userId}/rights`);
      return response;
    } catch (error) {
      console.error('Error fetching user admin rights:', error);
      throw error;
    }
  }

  static async updateUserAdminRights(userId: string, adminRights: string[]): Promise<void> {
    try {
      await apiService.adminRequest(`/admin/users/${userId}/rights`, {
        method: 'PUT',
        body: JSON.stringify({
          admin_rights: adminRights
        })
      });
    } catch (error) {
      console.error('Error updating user admin rights:', error);
      throw error;
    }
  }

  // Program Config Management
  static async getProgramConfig(programId: string): Promise<ProgramConfig> {
    try {
      // Public endpoint - use fetch directly
      const response = await fetch(`${getApiBaseUrl()}/programs/${programId}/config`);
      if (!response.ok) {
        throw new Error(`Failed to fetch program config: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching program config:', error);
      throw error;
    }
  }

  static async getProgramConfigAdmin(programId: string): Promise<ProgramConfig> {
    try {
      const response = await apiService.adminRequest(`/admin/programs/${programId}/config`);
      return response;
    } catch (error) {
      console.error('Error fetching program config (admin):', error);
      throw error;
    }
  }

  static async updateProgramConfig(programId: string, config: any, changeDescription?: string): Promise<any> {
    try {
      const response = await apiService.adminRequest(`/admin/programs/${programId}/config`, {
        method: 'PUT',
        body: JSON.stringify({
          config,
          change_description: changeDescription
        })
      });
      return response;
    } catch (error) {
      console.error('Error updating program config:', error);
      throw error;
    }
  }

  static async uploadProgramImage(file: File, programId: string = 'saa'): Promise<{url: string; key: string}> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('program_id', programId);

      const response = await apiService.adminRequest('/admin/programs/upload-image', {
        method: 'POST',
        body: formData,
        headers: {} // Let browser set Content-Type for FormData
      });
      return response;
    } catch (error) {
      console.error('Error uploading program image:', error);
      throw error;
    }
  }

  static async uploadProgramLogo(file: File, programId: string = 'saa'): Promise<{url: string; key: string}> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('program_id', programId);

      const response = await apiService.adminRequest('/admin/programs/upload-logo', {
        method: 'POST',
        body: formData,
        headers: {} // Let browser set Content-Type for FormData
      });
      return response;
    } catch (error) {
      console.error('Error uploading program logo:', error);
      throw error;
    }
  }

  static async uploadProgramDocument(file: File, programId: string): Promise<{url: string; key: string}> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('program_id', programId);

      const response = await apiService.adminRequest('/admin/programs/upload-document', {
        method: 'POST',
        body: formData,
        headers: {} // Let browser set Content-Type for FormData
      });
      return response;
    } catch (error) {
      console.error('Error uploading program document:', error);
      throw error;
    }
  }

  static async getProgramHistory(programId: string, limit: number = 50): Promise<ProgramHistoryEntry[]> {
    try {
      const response = await apiService.adminRequest(`/admin/programs/${programId}/history?limit=${limit}`);
      return response.history || [];
    } catch (error) {
      console.error('Error fetching program history:', error);
      throw error;
    }
  }

  /** Site config (home page tagline, bullets, meetings + workshop flags) */
  static async getSiteConfig(): Promise<{
    tagline: string;
    section_heading: string;
    bullet1: string;
    bullet2: string;
    bullet3: string;
    meetings_enabled: boolean;
    workshop_2026_meeting_code?: string;
    workshops_tab_show_full_finder?: boolean;
  }> {
    const response = await apiService.adminRequest('/admin/site/config');
    return response;
  }

  static async updateSiteConfig(config: {
    tagline: string;
    section_heading: string;
    bullet1: string;
    bullet2: string;
    bullet3: string;
    meetings_enabled: boolean;
    workshop_2026_meeting_code?: string;
    workshops_tab_show_full_finder?: boolean;
  }): Promise<void> {
    await apiService.adminRequest('/admin/site/config', {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }
} 